import { useMemo } from 'react'
import { SelectItem } from '@/components/SingleItemSelector'
import {
  CustomSelect,
  type RenderSearchItemProps,
} from '@/components/inputs/CustomSelect'
import { ItemSource } from '@/components/inputs/custom-select-state-observable'
import { useCustomSelectStateFromItemSource } from '@/components/inputs/item-source-hook'
import {
  areSelectOptionsEqual,
  type SelectOption,
} from '@/components/inputs/select-option'

interface OptionSelectProps {
  options: readonly SelectOption[]
  placeholder?: string
  isDisabled?: boolean
  isInvalid?: boolean
  testId?: string
}

export interface MultiOptionSelectProps extends OptionSelectProps {
  /**
   * The IDs of the picked options.
   */
  value: readonly string[]
  onChange: (ids: string[]) => unknown
}

/**
 * Picks several options by ID, searching by label.
 */
export function MultiOptionSelect({
  value,
  options,
  onChange,
  placeholder,
  isDisabled,
  isInvalid,
  testId,
}: MultiOptionSelectProps) {
  const selectProps = useOptionSearch(options)
  return (
    <CustomSelect
      {...selectProps}
      multi
      persist
      value={value.map((id) => findOption(options, id))}
      onChange={(next) => onChange((next ?? []).map((o) => o.id))}
      itemToString={itemToString}
      areItemsEqual={areSelectOptionsEqual}
      placeholder={placeholder}
      isDisabled={isDisabled}
      status={isInvalid ? 'danger' : 'default'}
      testId={testId}
      renderSearchItem={renderOption}
    />
  )
}

export interface SingleOptionSelectProps extends OptionSelectProps {
  /**
   * The ID of the picked option; empty when none is.
   */
  value: string
  onChange: (id: string) => unknown
}

/**
 * Picks one option by ID, searching by label. Once picked, an option can
 * only be replaced, not cleared.
 */
export function SingleOptionSelect({
  value,
  options,
  onChange,
  placeholder,
  isDisabled,
  isInvalid,
  testId,
}: SingleOptionSelectProps) {
  const selectProps = useOptionSearch(options)
  return (
    <CustomSelect
      {...selectProps}
      multi={false}
      value={value ? findOption(options, value) : undefined}
      onChange={(next) => next && onChange(next.id)}
      itemToString={itemToString}
      areItemsEqual={areSelectOptionsEqual}
      placeholder={placeholder}
      isDisabled={isDisabled}
      status={isInvalid ? 'danger' : 'default'}
      testId={testId}
      renderSearchItem={renderOption}
    />
  )
}

function useOptionSearch(options: readonly SelectOption[]) {
  const itemSource = useMemo(
    () => ItemSource.static([...options], matchesLabel),
    [options],
  )
  return useCustomSelectStateFromItemSource({ itemSource })
}

const matchesLabel = (option: SelectOption, text: string) =>
  option.text.toLowerCase().includes(text.toLowerCase())

/**
 * A picked ID that is no longer offered shows as the ID, so that it isn't
 * dropped by accident.
 */
function findOption(options: readonly SelectOption[], id: string) {
  return options.find((o) => o.id === id) ?? { id, text: id }
}

const itemToString = (option: SelectOption | undefined) => option?.text ?? ''

const renderOption = ({
  item,
  ...itemProps
}: RenderSearchItemProps<SelectOption>) => (
  <SelectItem key={item.id} {...itemProps}>
    {item.text}
  </SelectItem>
)
